<template>
  <div class="tab-workspace" @click="onCloseContextMenu">
    <!-- ==================== 페이지 머리 (화면명 + 탭 pill + actions 슬롯) ==================== -->
    <!-- 탭이 1개면 탭 pill 줄을 넘기지 않는다 — 제목 옆에 같은 글자 상자가 겹쳐 보이던 문제 (SURINP-688) -->
    <page-header>
      <template #title><slot name="title" /></template>
      <template v-if="tabs.length > 1" #tabs>
        <div class="rd-tab-pills-row" ref="tabBarRef" @wheel.prevent="onWheel">
          <div
            v-for="(tab, index) in tabs"
            :key="tab.id"
            class="rd-tab-pill"
            :class="{
              'rd-tab-pill--active': tab.id === activeTabId,
              'rd-tab-pill--drag-over': dragOverIndex === index,
              'rd-tab-pill--pinned': tab.pinned
            }"
            :draggable="!tab.pinned"
            :title="tab.title"
            @click="onTabClick(tab)"
            @contextmenu.prevent="onContextMenu($event, tab)"
            @dragstart="onDragStart($event, index)"
            @dragover.prevent="onDragOver(index)"
            @dragend="onDragEnd"
            @drop.prevent="onDrop(index)"
          >
            <span class="rd-tab-pill__title">{{ tab.title }}</span>
            <button
              v-if="tabs.length > 1 && !tab.pinned"
              class="rd-tab-pill__close"
              @click.stop="onTabClose(tab)"
              aria-label="탭 닫기"
            >&times;</button>
          </div>
        </div>
      </template>
      <template #actions><slot name="actions" /></template>
    </page-header>
    <!-- ==================== 라우터 뷰 (keep-alive 포함) ==================== -->
    <router-view v-slot="{ Component, route }">
      <keep-alive :max="MAX_CACHE">
        <component :is="Component" :key="route.fullPath" />
      </keep-alive>
    </router-view>
    <TabContextMenu
      :visible="contextMenu.visible"
      :x="contextMenu.x"
      :y="contextMenu.y"
      :tab-id="contextMenu.tabId"
      :tabs="tabs"
      @close="onCloseContextMenu"
      @navigate="onContextNavigate"
    />
  </div>
</template>

<script>
import { useTabStore, MAX_CACHE } from '@/stores/tabs'
import TabContextMenu from './TabContextMenu.vue'
import PageHeader from './PageHeader.vue'

/**
 * @component TabWorkspace
 * @description 탭 기반 워크스페이스 컴포넌트.
 * 페이지 머리(PageHeader — 화면명 + 탭 pill + actions 슬롯)와 router-view(keep-alive) 를 포함합니다.
 * 탭 pill 줄은 탭이 2개 이상일 때만 그립니다 (SURINP-688).
 * title / actions 두 개의 named slot 으로 App.vue 가 화면명과 우측 액션(PartnerSelect 등)을 주입합니다.
 */
export default {
  name: 'TabWorkspace',
  // ── 컴포넌트 등록 ──
  components: { TabContextMenu, PageHeader },
  setup() {
    const tabStore = useTabStore()
    return { tabStore, MAX_CACHE }
  },
  // ── 반응형 데이터 ──
  data() {
    return {
      /** 드래그 시작 탭 인덱스 */
      dragIndex: null,
      /** 드래그 진입 중인 탭 인덱스 */
      dragOverIndex: null,
      /** 우클릭 컨텍스트 메뉴 상태 */
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        tabId: null
      }
    }
  },
  // ── 계산된 속성 ──
  computed: {
    /**
     * Pinia 탭 스토어에서 전체 탭 목록을 반환합니다.
     */
    tabs() {
      return this.tabStore.tabs
    },
    /**
     * 현재 활성 탭의 ID를 반환합니다.
     */
    activeTabId() {
      return this.tabStore.activeTabId
    }
  },
  // ── 메서드 ──
  methods: {
    /**
     * 탭 클릭 시 해당 탭을 활성화하고 경로로 이동합니다.
     * @param {Object} tab - 클릭한 탭 객체
     */
    onTabClick(tab) {
      if (tab.id === this.activeTabId) return
      this.tabStore.setActive(tab.id)
      this.$router.push(tab.fullPath)
    },
    /**
     * 탭 닫기 버튼 클릭 시 해당 탭을 닫고 다음 탭으로 이동합니다.
     * @param {Object} tab - 닫을 탭 객체
     */
    onTabClose(tab) {
      const nextPath = this.tabStore.closeTab(tab.id)
      if (nextPath) {
        this.$router.push(nextPath)
      }
    },
    /**
     * 탭 우클릭 시 클릭 좌표에 컨텍스트 메뉴를 표시합니다.
     * @param {MouseEvent} event - 우클릭 마우스 이벤트
     * @param {Object} tab - 우클릭된 탭 객체
     */
    onContextMenu(event, tab) {
      this.contextMenu = {
        visible: true,
        x: event.clientX,
        y: event.clientY,
        tabId: tab.id
      }
    },
    /**
     * 컨텍스트 메뉴를 닫습니다.
     */
    onCloseContextMenu() {
      this.contextMenu = { ...this.contextMenu, visible: false }
    },
    /**
     * 컨텍스트 메뉴에서 경로 이동 요청 시 라우터로 이동합니다.
     * @param {string} path - 이동할 경로
     */
    onContextNavigate(path) {
      this.$router.push(path)
    },
    /**
     * 드래그 시작 시 드래그 인덱스를 설정합니다.
     * @param {DragEvent} event
     * @param {number} index - 드래그 시작 탭 인덱스
     */
    onDragStart(event, index) {
      this.dragIndex = index
      event.dataTransfer.effectAllowed = 'move'
    },
    /**
     * 다른 탭 위로 드래그 진입 시 드롭 대상 인덱스를 설정합니다.
     * @param {number} index - 드래그 진입 탭 인덱스
     */
    onDragOver(index) {
      this.dragOverIndex = index
    },
    /**
     * 드롭 완료 시 탭 순서를 변경합니다.
     * @param {number} index - 드롭 위치 탭 인덱스
     */
    onDrop(index) {
      if (this.dragIndex !== null && this.dragIndex !== index) {
        this.tabStore.moveTab(this.dragIndex, index)
      }
      this.dragIndex = null
      this.dragOverIndex = null
    },
    /**
     * 드래그 종료 시 드래그 상태를 초기화합니다.
     */
    onDragEnd() {
      this.dragIndex = null
      this.dragOverIndex = null
    },
    /**
     * 탭 바 위에서 마우스 휠 스크롤 시 탭 바를 가로 스크롤합니다.
     * @param {WheelEvent} event
     */
    onWheel(event) {
      if (this.$refs.tabBarRef) {
        this.$refs.tabBarRef.scrollLeft += event.deltaY
      }
    }
  }
}
</script>

<style scoped>
/* Pill / title-bar 스타일은 전역 layout.css 에서 관리. 이 컴포넌트는 구조만 담당. */
.tab-workspace {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}
</style>
